/**
 * The Action Register row a Verification Log row points at (`RecordRef = ActionRegister/<id>`),
 * shown inline so the verifier sees what was actually done (13-11, the mock's `.linked` box).
 *
 *   - compact (in the Record cell): title, `ACT-<id>`, owner role, status;
 *   - `detail` (the expanded row): plus the evidence, when it was completed, and WHO completed it,
 *     resolved through 13-07's `loadVerifyContext` (ISS-188). An unknown completer is said out
 *     loud ("who completed this can't be confirmed"), never left blank.
 *
 * Pure presentation: the page owns the reads (one Action Register read for every linked row,
 * then one audit read per completed linked row).
 */

import type { LinkedState } from '../../lib/vlVerbs';
import { needsCompleter } from '../../lib/vlVerbs';
import type { OwnerRole } from '../../types/actionRegister';
import styles from './VerifyPage.module.css';

const ROLE_LABEL: Record<OwnerRole, string> = {
  Oversight: 'Oversight',
  CareManager: 'Care Manager',
  HRWHS: 'HR & WHS',
  Finance: 'Finance',
  Admin: 'Admin',
  Board: 'Board',
  QCAC: 'QCAC',
  Wellness: 'Wellness Committee',
  DAG: 'Diversity Action Group',
};

const MELBOURNE = new Intl.DateTimeFormat('en-AU', {
  timeZone: 'Australia/Melbourne',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
});

function when(iso: string | null): string | null {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? null : MELBOURNE.format(t);
}

const EVIDENCE_MAX = 600;
/** The details excerpt on the compact card (the full text is in the expanded row). */
const ABOUT_SHORT = 150;

const DAY = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Melbourne', day: 'numeric', month: 'short' });

/** `DueDate` is date-only, stored as Melbourne midnight in UTC; shown as "16 Oct". */
function dueOn(iso: string | null): string | null {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? null : DAY.format(t);
}

/** Cut at a word boundary, with an ellipsis. */
function excerpt(text: string, max: number): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

interface Props {
  link: LinkedState;
  detail?: boolean;
}

export default function LinkedAction({ link, detail = false }: Props) {
  if (link.status === 'none') return null;

  if (link.status !== 'ready') {
    const text =
      link.status === 'loading'
        ? 'Loading the linked action…'
        : link.status === 'missing'
          ? `${link.ref} isn't in the Action Register.`
          : `${link.ref} couldn't be read.`;
    return (
      <div className={`${styles.linked} ${styles.linkedQuiet}`}>
        <span className={styles.lk}>Action</span>
        <div className={styles.sub}>{text}</div>
      </div>
    );
  }

  const { row, completer } = link;
  const role = ROLE_LABEL[row.ownerRole] ?? row.ownerRole;

  const about = (row.description ?? '').trim();
  const due = dueOn(row.dueDate);

  if (!detail) {
    // 13.1-14 (ISS-241): the details travel with the title, so the verifier can place the action without opening it.
    return (
      <div className={styles.linked}>
        <span className={styles.lk}>Action</span>
        <div>
          <b>{row.title || row.reference}</b>
          {about ? <div className={styles.about}>{excerpt(about, ABOUT_SHORT)}</div> : null}
          <div className={styles.sub}>
            {row.reference}, {role}, status {row.status}
            {due ? `, due ${due}` : ''}
          </div>
        </div>
      </div>
    );
  }

  const evidence = (row.evidence ?? '').trim();
  const completedAt = when(row.completedAt);
  let completedBy: string;
  if (!needsCompleter(row)) completedBy = 'Not completed yet.';
  else if (completer === 'loading') completedBy = 'Checking the audit trail…';
  else if (completer === 'error') completedBy = "The audit trail couldn't be read.";
  else if (completer === 'not-needed' || !completer.completedByUpn) {
    completedBy = "Who completed this can't be confirmed: the audit trail has no Done entry for it.";
  } else completedBy = completer.completedByUpn;

  return (
    <dl className={styles.facts}>
      <dt>Linked action</dt>
      <dd>
        <b>{row.reference}</b> {row.title}
      </dd>
      <dt>What it asks</dt>
      <dd className={styles.evidence}>{about || 'No details were written with this action.'}</dd>
      <dt>Owner role</dt>
      <dd>{role}</dd>
      {due ? (
        <>
          <dt>Due</dt>
          <dd>{due}</dd>
        </>
      ) : null}
      <dt>Status</dt>
      <dd>
        {row.status}
        {row.releasedAt ? null : ', not yet released'}
      </dd>
      {completedAt ? (
        <>
          <dt>Completed</dt>
          <dd>{completedAt}</dd>
        </>
      ) : null}
      <dt>Completed by</dt>
      <dd>{completedBy}</dd>
      <dt>Evidence</dt>
      <dd className={styles.evidence}>
        {evidence
          ? evidence.length > EVIDENCE_MAX
            ? `${evidence.slice(0, EVIDENCE_MAX)}…`
            : evidence
          : 'None recorded on the action.'}
      </dd>
    </dl>
  );
}
